import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { PlatformSubSection } from "@tracabois/template"
import { Stack } from "@mui/material"
import { InputForm } from "@komune-io/g2-forms"
import { Realization, RealizationUpdateCommand, SIZE_CATEGORY_LABELS } from "../../../model/realizationModel"
import { SelectFormField } from "../../SelectFormField"

interface RealizationBdaSectionProps {
  realization?: Realization
  formValues: Partial<RealizationUpdateCommand>
  onChange: (values: Partial<RealizationUpdateCommand>) => void
  readOnly?: boolean
}

const useLabels = () => {
  const { t } = useTranslation()
  return useMemo(() => ({
    title: t("realization.bda.title", "Autres informations"),
    surface: t("realization.surface", "Surface (m²)"),
    surfaceRetained: t("realization.surfaceRetained", "Surface retenue (m²)"),
    volume: t("realization.volume", "Volume (m³)"),
    volumeDescription: t("realization.volumeDescription", "Volume certifié"),
    sizeCategory: t("realization.sizeCategory", "Catégorie de taille"),
  }), [t])
}

export const RealizationBdaSection = (props: RealizationBdaSectionProps) => {
  const { formValues, onChange, readOnly = false } = props
  const labels = useLabels()

  const handleNumberChange = (field: keyof RealizationUpdateCommand) => (value: string | number | undefined) => {
    const numValue = typeof value === 'string' ? (value ? parseFloat(value) : undefined) : value
    onChange({ ...formValues, [field]: numValue })
  }

  const handleChange = (field: keyof RealizationUpdateCommand) => (value: string | number | undefined) => {
    onChange({ ...formValues, [field]: value })
  }

  return (
    <PlatformSubSection title={labels.title}>
      <Stack spacing={2}>
        <InputForm
          inputType="textField"
          label={labels.surface}
          value={formValues.surface?.toString() || ""}
          onChange={handleNumberChange("surface")}
          readOnly={readOnly}
        />
        <InputForm
          inputType="textField"
          label={labels.surfaceRetained}
          value={formValues.surfaceRetained?.toString() || ""}
          onChange={handleNumberChange("surfaceRetained")}
          readOnly={readOnly}
        />
        <InputForm
          inputType="textField"
          label={labels.volume}
          value={formValues.volume?.toString() || ""}
          onChange={handleNumberChange("volume")}
          readOnly={readOnly}
        />
        <InputForm
          inputType="textField"
          label={labels.volumeDescription}
          value={formValues.volumeDescription || ""}
          onChange={handleChange("volumeDescription")}
          readOnly={readOnly}
        />
        <SelectFormField
          label={labels.sizeCategory}
          value={formValues.sizeCategory}
          labels={SIZE_CATEGORY_LABELS}
          onChange={(val) => onChange({ ...formValues, sizeCategory: val as any })}
          readOnly={readOnly}
        />
      </Stack>
    </PlatformSubSection>
  )
}
